/**
 * Hooks for collection target management
 *
 * Provides hooks for listing targets with filtering, dashboard summary,
 * bulk status updates, and endpoint collection history.
 */

import { useState, useEffect, useCallback } from 'react'
import { useInvalidationSubscription } from './use-list-invalidation'
import type {
  CollectionTarget,
  CollectionTargetSummary,
  BulkStatusUpdateInput,
  BulkStatusUpdateResult,
  ListTargetsParams,
  EndpointCollectionHistory,
} from '../types/collection-targets'
import {
  listTargets,
  bulkUpdateStatus,
  getTargetSummary,
  getEndpointCollectionHistory,
} from '../services/collection-targets'
import { addTargets, removeTargets } from '../services/collection-requests'
import type { AddTargetsResult, RemoveTargetsResult } from '../types/collection-requests'

// =============================================================================
// useCollectionTargets - List targets for a collection request
// =============================================================================

interface UseCollectionTargetsResult {
  targets: CollectionTarget[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch collection targets for a request
 */
export function useCollectionTargets(
  caseId: string | null,
  requestId: string | null,
  params?: ListTargetsParams
): UseCollectionTargetsResult {
  const [targets, setTargets] = useState<CollectionTarget[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId || !requestId) {
      setTargets([])
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await listTargets(caseId, requestId, params)
      setTargets(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch collection targets')
      setTargets([])
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable individual param values
  }, [caseId, requestId, params?.status, params?.hostname, params?.priority])

  useEffect(() => {
    void fetch()
  }, [fetch])

  useInvalidationSubscription('collection_targets', fetch)

  return { targets, loading, error, refetch: fetch }
}

// =============================================================================
// useTargetSummary - Dashboard summary for a collection request
// =============================================================================

interface UseTargetSummaryResult {
  summary: CollectionTargetSummary | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch target dashboard summary
 */
export function useTargetSummary(
  caseId: string | null,
  requestId: string | null
): UseTargetSummaryResult {
  const [summary, setSummary] = useState<CollectionTargetSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId || !requestId) {
      setSummary(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getTargetSummary(caseId, requestId)
      setSummary(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch target summary')
      setSummary(null)
    } finally {
      setLoading(false)
    }
  }, [caseId, requestId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { summary, loading, error, refetch: fetch }
}

// =============================================================================
// useBulkStatusUpdate - Bulk update target statuses
// =============================================================================

interface UseBulkStatusUpdateResult {
  updating: boolean
  error: string | null
  update: (
    caseId: string,
    requestId: string,
    args: BulkStatusUpdateInput
  ) => Promise<BulkStatusUpdateResult | null>
  clearError: () => void
}

/**
 * Hook for bulk status update operations
 */
export function useBulkStatusUpdate(): UseBulkStatusUpdateResult {
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const update = useCallback(
    async (
      caseId: string,
      requestId: string,
      args: BulkStatusUpdateInput
    ): Promise<BulkStatusUpdateResult | null> => {
      setUpdating(true)
      setError(null)

      try {
        return await bulkUpdateStatus(caseId, requestId, args)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update target statuses')
        return null
      } finally {
        setUpdating(false)
      }
    },
    []
  )

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return { updating, error, update, clearError }
}

// =============================================================================
// useEndpointCollectionHistory - History for a specific endpoint
// =============================================================================

interface UseEndpointCollectionHistoryResult {
  history: EndpointCollectionHistory | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch collection history for an endpoint
 */
export function useEndpointCollectionHistory(
  caseId: string | null,
  endpointId: string | null
): UseEndpointCollectionHistoryResult {
  const [history, setHistory] = useState<EndpointCollectionHistory | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId || !endpointId) {
      setHistory(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getEndpointCollectionHistory(caseId, endpointId)
      setHistory(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch endpoint collection history')
      setHistory(null)
    } finally {
      setLoading(false)
    }
  }, [caseId, endpointId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { history, loading, error, refetch: fetch }
}

// =============================================================================
// useCollectionTargetMutations - Add/remove targets with lock awareness
// =============================================================================

interface UseCollectionTargetMutationsResult {
  addingTargets: boolean
  removingTargets: boolean
  error: string | null
  handleAddTargets: (endpointIds: string[]) => Promise<AddTargetsResult | null>
  handleRemoveTargets: (targetIds: string[]) => Promise<RemoveTargetsResult | null>
  clearError: () => void
}

/**
 * Hook for adding/removing targets from a collection request.
 *
 * Returns mutation functions that handle loading state, error handling,
 * and return results including duplicate detection for adds.
 */
export function useCollectionTargetMutations(
  caseId: string | null,
  requestId: string | null
): UseCollectionTargetMutationsResult {
  const [addingTargets, setAddingTargets] = useState(false)
  const [removingTargets, setRemovingTargets] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const handleAddTargets = useCallback(
    async (endpointIds: string[]): Promise<AddTargetsResult | null> => {
      if (!caseId || !requestId || endpointIds.length === 0) return null

      setAddingTargets(true)
      setError(null)

      try {
        const result = await addTargets(caseId, requestId, { endpointIds })
        return result
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to add targets'
        setError(message)
        return null
      } finally {
        setAddingTargets(false)
      }
    },
    [caseId, requestId]
  )

  const handleRemoveTargets = useCallback(
    async (targetIds: string[]): Promise<RemoveTargetsResult | null> => {
      if (!caseId || !requestId || targetIds.length === 0) return null

      setRemovingTargets(true)
      setError(null)

      try {
        const result = await removeTargets(caseId, requestId, { targetIds })
        return result
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to remove targets'
        setError(message)
        return null
      } finally {
        setRemovingTargets(false)
      }
    },
    [caseId, requestId]
  )

  return {
    addingTargets,
    removingTargets,
    error,
    handleAddTargets,
    handleRemoveTargets,
    clearError,
  }
}
